import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Tag,
  Button,
  Dropdown,
  Empty,
  Modal,
  message,
  Descriptions,
  Spin,
} from 'antd';
import {
  MoreOutlined,
  EyeOutlined,
  DownloadOutlined,
  EditOutlined,
  MailOutlined,
  BellOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  SendOutlined,
  FileAddOutlined,
  CopyOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { financeSettingsApi } from '../../utils/api';
import { toJalali } from '../../utils/dateUtils';
import { formatClientNameWithCompany, resolveClientCompanyName } from '../../utils/clientDisplayUtils';
import { formatMoneyFa as formatMoney } from '../../utils/formatPriceToman';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PANEL_DANGER_OK_BUTTON_PROPS } from '../../constants/ui';

/** پرمیشن invoices/estimates فقط با GET همان لیست ثبت می‌شود — در تب embedded بootstrap می‌کنیم */
const useEmbeddedFinancePermissions = (resourceKey, permissionsProp) => {
  const { getResourcePermissions } = usePermissions();
  const bootstrappedRef = useRef(false);
  const resourcePerms = useResourcePermissions(resourceKey, permissionsProp);

  useEffect(() => {
    if (permissionsProp) return undefined;
    if (getResourcePermissions(resourceKey)) return undefined;
    if (bootstrappedRef.current) return undefined;

    bootstrappedRef.current = true;
    const request =
      resourceKey === 'invoices'
        ? financeSettingsApi.getInvoices({ status: 'all', page: 1, per_page: 1 })
        : financeSettingsApi.getEstimates({ status: 'all', page: 1, per_page: 1 });

    request.catch(() => {});
    return undefined;
  }, [resourceKey, permissionsProp, getResourcePermissions]);

  return resourcePerms;
};

const invoiceStatusTag = {
  unpaid: { color: 'red', text: 'پرداخت نشده' },
  paid: { color: 'green', text: 'پرداخت شده' },
  partial: { color: 'orange', text: 'پرداخت ناقص' },
  canceled: { color: 'default', text: 'لغو شده' },
  cancelled: { color: 'default', text: 'لغو شده' },
  draft: { color: 'blue', text: 'پیش‌نویس' },
  'pending-confirmation': { color: 'processing', text: 'در انتظار تایید' },
};

const INVOICE_STATUS_CHANGE_OPTIONS = [
  { value: 'paid', label: 'پرداخت شده', color: 'green' },
  { value: 'unpaid', label: 'پرداخت نشده', color: 'red' },
  { value: 'partial', label: 'پرداخت ناقص', color: 'orange' },
  { value: 'canceled', label: 'لغو شده', color: 'default' },
  { value: 'draft', label: 'پیش‌نویس', color: 'blue' },
  { value: 'pending-confirmation', label: 'در انتظار تایید', color: 'processing' },
];

const estimateStatusTag = {
  waiting: { color: 'orange', text: 'در انتظار' },
  accepted: { color: 'green', text: 'پذیرفته شده' },
  declined: { color: 'red', text: 'رد شده' },
  draft: { color: 'blue', text: 'پیش‌نویس' },
  canceled: { color: 'default', text: 'لغو شده' },
  cancelled: { color: 'default', text: 'لغو شده' },
};

const ESTIMATE_STATUS_CHANGE_OPTIONS = [
  { value: 'waiting', label: 'در انتظار', color: 'orange' },
  { value: 'accepted', label: 'پذیرفته شده', color: 'green' },
  { value: 'declined', label: 'رد شده', color: 'red' },
  { value: 'draft', label: 'پیش‌نویس', color: 'blue' },
  { value: 'canceled', label: 'لغو شده', color: 'default' },
];

const isCanceledInvoice = (record) =>
  ['canceled', 'cancelled'].includes(String(record?.status || '').toLowerCase());

const currencyOf = (r) => r?.currency?.currency_symbol || r?.currency_symbol || '';

const formatDocDate = (value) => {
  if (!value) return '—';
  const jalali = toJalali(value);
  return jalali || '—';
};

const resolveDealLabel = (record) =>
  record?.deal?.name ||
  record?.deal?.deal_name ||
  (record?.deal_id != null ? `معامله #${record.deal_id}` : null);

const DealCell = ({ record, navigateWithTrail }) => {
  const label = resolveDealLabel(record);
  const dealId = record?.deal?.id ?? record?.deal_id;
  if (!label) return <span style={{ fontFamily: 'CustomFont' }}>—</span>;
  if (!dealId || !navigateWithTrail) {
    return <span style={{ fontFamily: 'CustomFont' }}>{label}</span>;
  }
  return (
    <button
      type="button"
      className="text-blue-600 hover:underline bg-transparent border-0 p-0 cursor-pointer"
      style={{ fontFamily: 'CustomFont' }}
      onClick={() => navigateWithTrail(`/deals/${dealId}`, label)}
    >
      {label}
    </button>
  );
};

export const buildInvoiceCreatePath = ({ clientId, dealId } = {}) => {
  const params = new URLSearchParams();
  if (clientId != null && clientId !== '') params.set('client_id', String(clientId));
  if (dealId != null && dealId !== '') params.set('deal_id', String(dealId));
  const qs = params.toString();
  return qs ? `/finance/invoices/add?${qs}` : '/finance/invoices/add';
};

export const buildEstimateCreatePath = ({ clientId, dealId } = {}) => {
  const params = new URLSearchParams();
  if (clientId != null && clientId !== '') params.set('client_id', String(clientId));
  if (dealId != null && dealId !== '') params.set('deal_id', String(dealId));
  const qs = params.toString();
  return qs ? `/finance/estimates/add?${qs}` : '/finance/estimates/add';
};

export function EmbeddedInvoicesTable({
  invoices = [],
  showDealColumn = false,
  showProjectColumn = true,
  clientId,
  dealId,
  onChanged,
  createEnabled = true,
  navigateWithTrail: navigateWithTrailProp,
  returnTo,
  actionsIconOnly = false,
  permissions: permissionsProp,
}) {
  const navigate = useNavigate();
  const { canAdd, canEditRecord, canDeleteRecord } = useEmbeddedFinancePermissions(
    'invoices',
    permissionsProp,
  );
  const goDetail = (path, label) => {
    if (navigateWithTrailProp) navigateWithTrailProp(path, label);
    else navigate(path);
  };
  const goCreate = (path) => {
    navigate(path, returnTo ? { state: { returnTo } } : undefined);
  };
  const [actionLoadingKey, setActionLoadingKey] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const list = Array.isArray(invoices) ? invoices : [];

  const getActionKey = (id, action) => `${id}-${action}`;
  const isActionLoading = (id, action) => actionLoadingKey === getActionKey(id, action);

  const changeInvoiceStatus = async (record, newStatus) => {
    if (String(record.status) === String(newStatus)) return;
    setStatusLoadingId(record.id);
    try {
      await financeSettingsApi.changeInvoiceStatus({
        invoiceId: record.id,
        status: newStatus,
      });
      const label = invoiceStatusTag[newStatus]?.text || newStatus;
      message.success(`وضعیت به «${label}» تغییر کرد`);
      onChanged?.();
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت فاکتور');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const runAction = async (id, action, fn, successMsg, errorMsg) => {
    setActionLoadingKey(getActionKey(id, action));
    try {
      await fn();
      if (successMsg) message.success(successMsg);
      if (action !== 'download' && action !== 'remind') onChanged?.();
    } catch (error) {
      message.error(error?.message || errorMsg);
    } finally {
      setActionLoadingKey(null);
    }
  };

  const confirmDelete = (record) => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف فاکتور؟</span>,
      content: <span style={{ fontFamily: 'CustomFont' }}>آیا از حذف این فاکتور اطمینان دارید؟</span>,
      okText: 'بله',
      cancelText: 'خیر',
      okType: 'primary',
      okButtonProps: PANEL_DANGER_OK_BUTTON_PROPS,
      onOk: () =>
        runAction(
          record.id,
          'delete',
          () => financeSettingsApi.deleteInvoice(record.id),
          'فاکتور حذف شد',
          'خطا در حذف فاکتور',
        ),
    });
  };

  const columns = [
    {
      title: 'شماره فاکتور',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v || '—'}</span>,
    },
    ...(showDealColumn
      ? [
          {
            title: 'معامله',
            key: 'deal',
            render: (_, r) => (
              <DealCell record={r} navigateWithTrail={navigateWithTrailProp || goDetail} />
            ),
          },
        ]
      : []),
    ...(showProjectColumn
      ? [
          {
            title: 'پروژه',
            key: 'project',
            render: (_, r) => (
              <span style={{ fontFamily: 'CustomFont' }}>
                {r.project?.project_name || r.project_name || '—'}
              </span>
            ),
          },
        ]
      : []),
    {
      title: 'تاریخ صدور',
      dataIndex: 'issue_date',
      key: 'issue_date',
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{formatDocDate(v)}</span>,
    },
    {
      title: 'سررسید',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{formatDocDate(v)}</span>,
    },
    {
      title: 'مبلغ کل',
      dataIndex: 'total',
      key: 'total',
      render: (v, r) => (
        <span style={{ fontFamily: 'CustomFont' }}>{formatMoney(v, currencyOf(r))}</span>
      ),
    },
    {
      title: 'مانده',
      dataIndex: 'due_amount',
      key: 'due_amount',
      render: (v, r) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {v != null ? formatMoney(v, currencyOf(r)) : '—'}
        </span>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (v, record) => {
        const current = String(v || '').toLowerCase() === 'cancelled' ? 'canceled' : v;
        const s = invoiceStatusTag[current] || invoiceStatusTag[v] || { color: 'default', text: v || '—' };
        const canChange = canEditRecord(record);
        if (!canChange) {
          return <Tag color={s.color} style={{ fontFamily: 'CustomFont' }}>{s.text}</Tag>;
        }
        const items = INVOICE_STATUS_CHANGE_OPTIONS.map((opt) => ({
          key: opt.value,
          label: (
            <span style={{ fontFamily: 'CustomFont' }}>
              <Tag color={opt.color} style={{ margin: 0, fontFamily: 'CustomFont' }}>{opt.label}</Tag>
              {opt.value === current ? <span className="text-xs text-gray-400 mr-2">✓</span> : null}
            </span>
          ),
        }));
        return (
          <Dropdown
            menu={{ items, onClick: ({ key }) => changeInvoiceStatus(record, key) }}
            trigger={['click']}
          >
            <Button type="text" size="small" loading={statusLoadingId === record.id} style={{ fontFamily: 'CustomFont' }}>
              <Tag color={s.color} style={{ fontFamily: 'CustomFont', marginInlineEnd: 4 }}>{s.text}</Tag>
              <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 130,
      align: 'center',
      render: (_, record) => {
        const isCanceled = isCanceledInvoice(record);
        const menuItems = [
          {
            key: 'view',
            label: <span style={{ fontFamily: 'CustomFont' }}>مشاهده</span>,
            icon: <EyeOutlined />,
            onClick: () =>
              goDetail(`/finance/invoices/${record.id}`, record.invoice_number || `فاکتور #${record.id}`),
          },
          {
            key: 'download',
            label: <span style={{ fontFamily: 'CustomFont' }}>دانلود</span>,
            icon: <DownloadOutlined />,
            disabled: isActionLoading(record.id, 'download'),
            onClick: () =>
              runAction(
                record.id,
                'download',
                () => financeSettingsApi.downloadInvoice(record.id),
                'دانلود فاکتور آغاز شد',
                'خطا در دانلود فاکتور',
              ),
          },
          ...(!isCanceled && canEditRecord(record)
            ? [
                {
                  key: 'edit',
                  label: <span style={{ fontFamily: 'CustomFont' }}>ویرایش</span>,
                  icon: <EditOutlined />,
                  onClick: () =>
                    goDetail(
                      `/finance/invoices/${record.id}/edit`,
                      record.invoice_number || `فاکتور #${record.id}`,
                    ),
                },
              ]
            : []),
          ...(!isCanceled
            ? [
                {
                  key: 'send',
                  label: <span style={{ fontFamily: 'CustomFont' }}>ارسال فاکتور</span>,
                  icon: <MailOutlined />,
                  disabled: isActionLoading(record.id, 'send'),
                  onClick: () =>
                    runAction(
                      record.id,
                      'send',
                      () => financeSettingsApi.sendInvoice(record.id),
                      'فاکتور ارسال شد',
                      'خطا در ارسال فاکتور',
                    ),
                },
                {
                  key: 'remind',
                  label: <span style={{ fontFamily: 'CustomFont' }}>یادآوری پرداخت</span>,
                  icon: <BellOutlined />,
                  disabled: isActionLoading(record.id, 'remind'),
                  onClick: () =>
                    runAction(
                      record.id,
                      'remind',
                      () => financeSettingsApi.sendInvoicePaymentReminder(record.id),
                      'یادآوری پرداخت ارسال شد',
                      'خطا در ارسال یادآوری',
                    ),
                },
                {
                  key: 'cancel',
                  label: <span style={{ fontFamily: 'CustomFont' }}>لغو فاکتور</span>,
                  icon: <CloseCircleOutlined />,
                  danger: true,
                  disabled: isActionLoading(record.id, 'cancel'),
                  onClick: () =>
                    runAction(
                      record.id,
                      'cancel',
                      () => financeSettingsApi.cancelInvoice(record.id),
                      'فاکتور لغو شد',
                      'خطا در لغو فاکتور',
                    ),
                },
              ]
            : []),
          ...(canDeleteRecord(record)
            ? [
                { type: 'divider' },
                {
                  key: 'delete',
                  label: <span style={{ fontFamily: 'CustomFont' }}>حذف</span>,
                  icon: <DeleteOutlined />,
                  danger: true,
                  disabled: isActionLoading(record.id, 'delete'),
                  onClick: () => confirmDelete(record),
                },
              ]
            : []),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button
              type="text"
              icon={<MoreOutlined />}
              loading={actionLoadingKey?.startsWith(`${record.id}-`)}
              style={{ fontFamily: 'CustomFont' }}
            >
              {actionsIconOnly ? null : 'عملیات'}
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      {createEnabled && canAdd ? (
        <div className="flex justify-end mb-3">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="bg-green-600 hover:bg-green-700 border-0"
            style={{ fontFamily: 'CustomFont' }}
            onClick={() => goCreate(buildInvoiceCreatePath({ clientId, dealId }))}
          >
            ایجاد فاکتور
          </Button>
        </div>
      ) : null}
      {list.length ? (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{ pageSize: 10 }}
          scroll={{ x: showDealColumn ? 1100 : 1000 }}
          size="middle"
        />
      ) : (
        <Empty description="فاکتوری ثبت نشده" />
      )}
    </div>
  );
}

export function EmbeddedEstimatesTable({
  estimates = [],
  showDealColumn = false,
  showProjectColumn = true,
  clientId,
  dealId,
  onChanged,
  createEnabled = true,
  navigateWithTrail: navigateWithTrailProp,
  returnTo,
  actionsIconOnly = false,
  permissions: permissionsProp,
}) {
  const navigate = useNavigate();
  const { canAdd, canEditRecord } = useEmbeddedFinancePermissions('estimates', permissionsProp);
  const goDetail = (path, label) => {
    if (navigateWithTrailProp) navigateWithTrailProp(path, label);
    else navigate(path);
  };
  const goCreate = (path) => {
    navigate(path, returnTo ? { state: { returnTo } } : undefined);
  };
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const list = Array.isArray(estimates) ? estimates : [];

  const changeEstimateStatus = async (record, newStatus) => {
    if (String(record.status) === String(newStatus)) return;
    setStatusLoadingId(record.id);
    try {
      await financeSettingsApi.updateEstimateStatus({
        estimateId: record.id,
        status: newStatus,
      });
      const label = estimateStatusTag[newStatus]?.text || newStatus;
      message.success(`وضعیت به «${label}» تغییر کرد`);
      onChanged?.();
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت پیش‌فاکتور');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const openViewModal = async (record) => {
    setViewModalVisible(true);
    setViewLoading(true);
    try {
      const res = await financeSettingsApi.getEstimate(record.id);
      setViewData(res?.data || null);
    } catch (error) {
      message.error('خطا در دریافت جزئیات پیش‌فاکتور');
      setViewModalVisible(false);
    } finally {
      setViewLoading(false);
    }
  };

  const sendEstimate = async (record) => {
    try {
      await financeSettingsApi.sendEstimate(record.id);
      message.success('پیش‌فاکتور ارسال شد');
      onChanged?.();
    } catch (error) {
      message.error(error?.message || 'خطا در ارسال پیش‌فاکتور');
    }
  };

  const downloadEstimate = async (record) => {
    try {
      await financeSettingsApi.downloadEstimate(record.id);
      message.success('دانلود پیش‌فاکتور آغاز شد');
    } catch (error) {
      message.error(error?.message || 'خطا در دانلود پیش‌فاکتور');
    }
  };

  const columns = [
    {
      title: 'شماره پیش‌فاکتور',
      dataIndex: 'estimate_number',
      key: 'estimate_number',
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v || '—'}</span>,
    },
    ...(showDealColumn
      ? [
          {
            title: 'معامله',
            key: 'deal',
            render: (_, r) => (
              <DealCell record={r} navigateWithTrail={navigateWithTrailProp || goDetail} />
            ),
          },
        ]
      : []),
    ...(showProjectColumn
      ? [
          {
            title: 'پروژه',
            key: 'project',
            render: (_, r) => (
              <span style={{ fontFamily: 'CustomFont' }}>
                {r.project?.project_name || r.project_name || '—'}
              </span>
            ),
          },
        ]
      : []),
    {
      title: 'تاریخ اعتبار',
      dataIndex: 'valid_till',
      key: 'valid_till',
      render: (v, r) => (
        <span style={{ fontFamily: 'CustomFont' }}>{formatDocDate(v || r.valid_date)}</span>
      ),
    },
    {
      title: 'مبلغ کل',
      dataIndex: 'total',
      key: 'total',
      render: (v, r) => (
        <span style={{ fontFamily: 'CustomFont' }}>{formatMoney(v, currencyOf(r))}</span>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (v, record) => {
        const current = String(v || '').toLowerCase() === 'cancelled' ? 'canceled' : v;
        const s = estimateStatusTag[current] || estimateStatusTag[v] || { color: 'default', text: v || '—' };
        const items = ESTIMATE_STATUS_CHANGE_OPTIONS.map((opt) => ({
          key: opt.value,
          label: (
            <span style={{ fontFamily: 'CustomFont' }}>
              <Tag color={opt.color} style={{ margin: 0, fontFamily: 'CustomFont' }}>{opt.label}</Tag>
              {opt.value === current ? <span className="text-xs text-gray-400 mr-2">✓</span> : null}
            </span>
          ),
        }));
        return (
          <Dropdown
            menu={{ items, onClick: ({ key }) => changeEstimateStatus(record, key) }}
            trigger={['click']}
          >
            <Button type="text" size="small" loading={statusLoadingId === record.id} style={{ fontFamily: 'CustomFont' }}>
              <Tag color={s.color} style={{ fontFamily: 'CustomFont', marginInlineEnd: 4 }}>{s.text}</Tag>
              <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 130,
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            label: 'مشاهده',
            icon: <EyeOutlined />,
            onClick: () => openViewModal(record),
          },
          {
            key: 'download',
            label: 'دانلود',
            icon: <DownloadOutlined />,
            onClick: () => downloadEstimate(record),
          },
          ...(canEditRecord(record)
            ? [
                {
                  key: 'edit',
                  label: 'ویرایش',
                  icon: <EditOutlined />,
                  onClick: () =>
                    goDetail(
                      `/finance/estimates/${record.id}/edit`,
                      record.estimate_number || `پیش‌فاکتور #${record.id}`,
                    ),
                },
              ]
            : []),
          {
            key: 'send',
            label: 'ارسال',
            icon: <SendOutlined />,
            onClick: () => sendEstimate(record),
          },
          {
            key: 'invoice',
            label: 'ایجاد فاکتور',
            icon: <FileAddOutlined />,
            onClick: () => goCreate(`/finance/invoices/add?estimate=${record.id}`),
          },
          {
            key: 'duplicate',
            label: 'ایجاد نسخه مشابه',
            icon: <CopyOutlined />,
            onClick: () => goCreate(`/finance/estimates/add?estimate=${record.id}`),
          },
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} style={{ fontFamily: 'CustomFont' }}>
              {actionsIconOnly ? null : 'عملیات'}
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const estimateView = viewData?.estimate || viewData;
  const estimateClientCompany = resolveClientCompanyName(
    estimateView?.client,
    viewData?.client,
    estimateView?.clientdetails,
    estimateView,
    viewData,
  );
  const estimateClientLabel =
    formatClientNameWithCompany(
      estimateView?.client,
      viewData?.client,
      estimateView?.clientdetails,
      estimateView,
      viewData,
    ) || null;

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      {createEnabled && canAdd ? (
        <div className="flex justify-end mb-3">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="bg-green-600 hover:bg-green-700 border-0"
            style={{ fontFamily: 'CustomFont' }}
            onClick={() => goCreate(buildEstimateCreatePath({ clientId, dealId }))}
          >
            ایجاد پیش‌فاکتور
          </Button>
        </div>
      ) : null}
      {list.length ? (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{ pageSize: 10 }}
          scroll={{ x: showDealColumn ? 1000 : 900 }}
          size="middle"
        />
      ) : (
        <Empty description="پیش‌فاکتوری ثبت نشده" />
      )}

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>جزئیات پیش‌فاکتور</span>}
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setViewData(null);
        }}
        footer={null}
        width={720}
        destroyOnHidden
      >
        {viewLoading ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : estimateView ? (
          <div style={{ fontFamily: 'CustomFont' }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="شماره">{estimateView.estimate_number || '—'}</Descriptions.Item>
              <Descriptions.Item label="وضعیت">
                {(() => {
                  const s = estimateStatusTag[estimateView.status] || {
                    color: 'default',
                    text: estimateView.status || '—',
                  };
                  return <Tag color={s.color}>{s.text}</Tag>;
                })()}
              </Descriptions.Item>
              {estimateClientLabel ? (
                <Descriptions.Item label="مشتری">{estimateClientLabel}</Descriptions.Item>
              ) : null}
              {estimateClientCompany ? (
                <Descriptions.Item label="نام شرکت">{estimateClientCompany}</Descriptions.Item>
              ) : null}
              <Descriptions.Item label="اعتبار تا">{formatDocDate(estimateView.valid_till)}</Descriptions.Item>
              <Descriptions.Item label="کل">
                {formatMoney(estimateView.total, currencyOf(estimateView))}
              </Descriptions.Item>
            </Descriptions>
          </div>
        ) : (
          <Empty description="اطلاعاتی یافت نشد" />
        )}
      </Modal>
    </div>
  );
}
